'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { Package } from 'lucide-react';
import {
  NestedSheet,
  NestedSheetContent,
  NestedSheetHeader,
  NestedSheetTitle,
  NestedSheetBody,
} from '@/shared/ui';
import { useMaterials } from '@/shared/lib/hooks/useMaterials';
import {
  useClassifiedProjectReferences,
  useProjectReferences,
} from '@/features/project-reference/hooks/use-project-references';
import {
  MaterialsContent,
  getMaterialsSubtitle,
} from '../settings-content/MaterialsContent';
import { SettingsSectionRow } from './SettingsSection';
import { ReferenceSummary } from './ReferenceSummary';

interface MaterialsSectionProps {
  selectedMaterialIds: number[] | null;
  onMaterialsChange: (materialIds: number[] | null) => void;
  hasChanges?: boolean;
  projectId?: string;
  materialProjectReferenceIds?: string[] | null;
  onMaterialProjectReferencesChange?: (ids: string[] | null) => void;
  useAutoMaterialRefs?: boolean;
  onUseAutoMaterialRefsChange?: (value: boolean) => void;
  onNavigateToReferences?: () => void;
  isRefine?: boolean;
}

export function MaterialsSection({
  selectedMaterialIds,
  onMaterialsChange,
  hasChanges = false,
  projectId,
  materialProjectReferenceIds,
  onMaterialProjectReferencesChange,
  useAutoMaterialRefs,
  onUseAutoMaterialRefsChange,
  onNavigateToReferences,
  isRefine = false,
}: MaterialsSectionProps) {
  const t = useTranslations('render.settings.materials');
  const { materials } = useMaterials();
  const { data: categoryRefs = [] } = useClassifiedProjectReferences(
    projectId ?? '',
    'material',
    { enabled: !!projectId }
  );
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  const hasSelectedMaterials = (selectedMaterialIds?.length ?? 0) > 0;

  // Selected references resolve by id across ALL references (any category):
  // the picker lets users select refs from other/unclassified categories, so a
  // category-scoped lookup dropped their thumbnails and the row wrongly showed
  // "no refs". Auto mode (allRefImages) stays category-scoped below.
  const { data: allRefs = [] } = useProjectReferences(projectId ?? '', {
    enabled: !!projectId,
  });
  const refImageUrl = (id: string) =>
    allRefs.find((r) => r.id === id)?.attributes.file_url ?? null;
  const selectedRefImages = (materialProjectReferenceIds ?? [])
    .map(refImageUrl)
    .filter((url): url is string => !!url);
  const allRefImages = categoryRefs
    .map((r) => r.attributes.file_url)
    .filter((url): url is string => !!url);

  // Materials have no thumbnails, so a library selection stays as text names;
  // references (manual or auto) render as circles via ReferenceSummary.
  const subtitle = hasSelectedMaterials
    ? getMaterialsSubtitle(selectedMaterialIds, materials, '')
    : undefined;
  const subtitleContent = hasSelectedMaterials ? undefined : (
    <ReferenceSummary
      selectedRefImages={selectedRefImages}
      allRefImages={allRefImages}
      useAutoRefs={useAutoMaterialRefs ?? true}
      isRefine={isRefine}
      autoLabel={t('autoMode')}
    />
  );

  return (
    <>
      <SettingsSectionRow
        icon={Package}
        title={t('title')}
        subtitle={subtitle}
        subtitleContent={subtitleContent}
        hasChanges={hasChanges}
        onClick={() => setIsSheetOpen(true)}
      />

      <NestedSheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
        <NestedSheetContent>
          <NestedSheetHeader>
            <NestedSheetTitle>{t('title')}</NestedSheetTitle>
          </NestedSheetHeader>

          <NestedSheetBody className="px-4">
            <MaterialsContent
              selectedMaterialIds={selectedMaterialIds}
              onMaterialsChange={onMaterialsChange}
              projectId={projectId}
              materialProjectReferenceIds={materialProjectReferenceIds}
              onMaterialProjectReferencesChange={
                onMaterialProjectReferencesChange
              }
              useAutoMaterialRefs={useAutoMaterialRefs}
              onUseAutoMaterialRefsChange={onUseAutoMaterialRefsChange}
              onNavigateToReferences={onNavigateToReferences}
              variant="compact"
            />
          </NestedSheetBody>
        </NestedSheetContent>
      </NestedSheet>
    </>
  );
}
